import { AxiosError } from 'axios';
import { getAxiosClient } from './http.client';
import { useFormInstance } from './fluidComponents/fluidForm/FormContext';
import { getFormInstance } from './fluidComponents/fluidForm/FormInstance.service';
export function buildFluidFormClient(signature) {
    return new FluidFormClient(signature);
}
export function useFluidFormClient() {
    const instanceId = useFormInstance();
    if (instanceId) {
        const instance = getFormInstance(instanceId);
        if (instance?.client) {
            return instance.client;
        }
    }
    return new FluidFormClient();
}
export class FluidFormClient {
    signature;
    apiClient;
    constructor(signature) {
        this.signature = signature;
    }
    async client() {
        if (this.apiClient) {
            return this.apiClient;
        }
        this.apiClient = getAxiosClient("fluid-form", this.signature);
        return this.apiClient;
    }
    async processAudio(formKey, file, submissionId, currentData) {
        const client = await this.client();
        const formData = new FormData();
        formData.append('file', file);
        formData.append('formKey', formKey);
        formData.append('submissionId', submissionId);
        formData.append('currentData', JSON.stringify(currentData));
        try {
            const response = await client.post(`/${formKey}/process-audio`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
            });
            return response.data;
        }
        catch (error) {
            console.error('Error processing audio:', error);
            throw error;
        }
    }
    ;
    async processImage(formKey, file, submissionId, currentData) {
        const client = await this.client();
        const formData = new FormData();
        formData.append('file', file);
        formData.append('formKey', formKey);
        formData.append('submissionId', submissionId);
        formData.append('currentData', JSON.stringify(currentData));
        try {
            const response = await client.post(`/${formKey}/process-image`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
            });
            return response.data;
        }
        catch (error) {
            console.error('Error processing image:', error);
            throw error;
        }
    }
    //process PDF
    async processPDF(formKey, file, submissionId, currentData) {
        const client = await this.client();
        const formData = new FormData();
        formData.append('file', file);
        formData.append('submissionId', submissionId);
        formData.append('currentData', JSON.stringify(currentData));
        formData.append('formKey', formKey);
        try {
            const response = await client.post(`/${formKey}/process-pdf`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
            });
            return response.data;
        }
        catch (error) {
            console.error('Error processing PDF:', error);
            throw error;
        }
    }
    //Get Form config
    async getFormConfig(formKey, submissionId) {
        const client = await this.client();
        try {
            const response = await client.get(`/${formKey}`, { params: submissionId ? { submissionId } : undefined });
            return response.data;
        }
        catch (error) {
            if (error instanceof AxiosError && error.response?.status === 404) {
                console.warn('could not find the form config as the form was not found:', formKey);
                throw new Error('Form not found');
            }
            console.error('Error getting form config:', error);
            throw error;
        }
    }
    // Scheduling: availability via natural language text
    async getAvailability(formKey, text, submissionId) {
        const client = await this.client();
        return (await client.post(`/${formKey}/availability`, { text, submissionId })).data;
    }
    async getSchedulingConfigurations() {
        const client = await this.client();
        return (await client.get(`/scheduling/configuration`)).data;
    }
    // Scheduling: reserve a slot
    async reserveSlot(params) {
        const client = await this.client();
        const body = {
            start: typeof params.start === 'string' ? params.start : params.start.toISOString(),
            end: typeof params.end === 'string' ? params.end : params.end.toISOString(),
            participants: params.participants,
        };
        return (await client.post(`/reserve`, body)).data;
    }
    async initializeSubmission(formKey, submissionId) {
        const client = await this.client();
        try {
            console.log("Initializing submission", formKey, submissionId);
            const response = await client.post(`${formKey}/initialize/${submissionId || ""}`, {});
            return response.data;
        }
        catch (error) {
            if (error instanceof AxiosError && error.response?.status === 404) {
                console.warn('Could not initialize the submission as the form was not found:', formKey, submissionId);
                throw new Error('Form not found');
            }
            console.error('Error initializing submission:', error);
            throw error;
        }
    }
    // Save submission without changing state (draft mode)
    async saveSubmission(formKey, submissionId, submissionData) {
        try {
            const client = await this.client();
            return (await client.post(`${formKey}/save/${submissionId}`, submissionData)).data;
        }
        catch (error) {
            console.error('Error saving submission:', error);
            throw error;
        }
    }
    //Submit Form
    async submitForm(formKey, data, currentStage, submissionId, confirmedFields) {
        try {
            const client = await this.client();
            return (await client.post(`${formKey}/submit/${submissionId}`, data, { params: { currentStage, confirmedFields } })).data;
        }
        catch (error) {
            console.error('Error submitting form:', error);
            throw error;
        }
    }
    // Field-level update (on blur or partial updates)
    async updateField(formKey, submissionId, submissionData) {
        if (!submissionId) {
            return;
        }
        try {
            const client = await this.client();
            return (await client.post(`/${formKey}/field-update/${submissionId}`, submissionData)).data;
        }
        catch (error) {
            console.error('Error updating field:', error);
            throw error;
        }
    }
    //Ask Form question
    async askFormQuestion(formKey, fieldId, question, messages, submissionId) {
        try {
            const client = await this.client();
            return (await client.post(`${formKey}/help`, { formKey, fieldId, question, messages, submissionId })).data;
        }
        catch (error) {
            console.error('Error asking form question:', error);
            throw error;
        }
    }
    //Send Chat Message
    async sendChatMessage(formKey, messageData, chatPath = "chat") {
        try {
            if (chatPath == "chat" && (!formKey || !messageData.threadId)) {
                console.error("Form key or thread id not set, cannot send chat message");
                return;
            }
            if (formKey === 'workspace-form' && chatPath === 'multiform-chat' && messageData.appendOnly) {
                console.info("Appending only, skipping chat message");
                return;
            }
            const client = await this.client();
            return (await client.post(`/${chatPath}/${formKey}`, messageData)).data;
        }
        catch (error) {
            console.error('Error sending chat message:', error);
            throw error;
        }
    }
    //Create Upload URL for file uploads
    async createUploadUrl(formKey, fieldPath, fileName, contentType) {
        try {
            const client = await this.client();
            return (await client.post(`/${formKey}/create-upload-url`, {
                fileName,
                contentType,
                fieldPath
            })).data;
        }
        catch (error) {
            console.error('Error creating upload URL:', error);
            throw error;
        }
    }
    async removeAttachment(formKey, fieldPath) {
        try {
            const client = await this.client();
            await client.post(`/${formKey}/remove-attachment`, { fieldPath });
        }
        catch (error) {
            console.error('Error removing attachment:', error);
            throw error;
        }
    }
    //Upload File to Storage
    async uploadFile(file, uploadUrl, fieldPath) {
        try {
            // Upload directly to Google Cloud Storage using the signed URL
            const response = await fetch(uploadUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': file.type,
                },
                body: file
            });
            if (!response.ok) {
                throw new Error(`Upload failed with status: ${response.status}`);
            }
        }
        catch (error) {
            console.error('Error uploading file:', error);
            throw error;
        }
    }
    // Get signed URL for file attachment by fileUri
    async getAttachmentSignedUrl(formKey, fileUri, fieldPath) {
        try {
            const client = await this.client();
            return (await client.post(`/${formKey}/preview-file`, {
                fieldPath,
                fileUri
            })).data;
        }
        catch (error) {
            console.error('Error getting attachment signed URL:', error);
            throw error;
        }
    }
    // Capture URL screenshot and return as blob
    async captureUrl(url) {
        try {
            const client = await this.client();
            const response = await client.post('/capture-url', { url }, {
                responseType: 'blob'
            });
            return response.data;
        }
        catch (error) {
            console.error('Error capturing URL:', error);
            throw error;
        }
    }
    // Count metric for a submission
    async countMetric(submissionId, metric, value) {
        try {
            const client = await this.client();
            client.post(`submission/${submissionId}/count`, { metric, value });
        }
        catch (error) {
            console.error('Error counting metric:', error);
        }
    }
    // Clear conversation history for a submission
    async clearConversationHistory(submissionId) {
        try {
            const client = await this.client();
            const response = await client.delete(`submission/${submissionId}/conversation`);
            return response.data;
        }
        catch (error) {
            console.error('Error clearing conversation history:', error);
            throw error;
        }
    }
    // Whatsapp: get display phone number
    async getWhatsAppDisplayPhoneNumber() {
        try {
            const client = await this.client();
            const response = await client.get(`/integrations/whatsapp/display-phone-number`);
            return response.data;
        }
        catch (error) {
            if (error.response?.status === 404 && error.response?.data?.error === 'Integration not found') {
                console.warn('WhatsApp integration not found, returning null for display phone number');
                return null;
            }
            console.error('Error fetching WhatsApp business display phone number:', error);
            throw error;
        }
    }
}
